import { useState } from 'react'
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Building2, Users, CreditCard, Layers, History, LogOut, Menu, X, Store } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { t, LanguageSwitcher } from '../../i18n'
import BrandLogo from '../BrandLogo'

const NAV = [
  { to: '/platform', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/platform/businesses', label: 'Businesses', icon: Building2 },
  { to: '/platform/users', label: 'Users', icon: Users },
  { to: '/platform/payments', label: 'Payments', icon: CreditCard },
  { to: '/platform/plans', label: 'Plans', icon: Layers },
  { to: '/platform/activity', label: 'Activity', icon: History },
]

export default function PlatformLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <BrandLogo showName={false} imageClassName="h-10 w-10" />
        <div>
          <p className="text-sm font-semibold text-white">{t('Rawaj Business Platform')}</p>
          <p className="text-xs text-slate-400">{t('Super admin')}</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
            <Icon className="h-4 w-4" />{t(label)}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-3 border-t border-white/10 p-4">
        <Link to="/owner" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><Store className="h-4 w-4" />{t('My businesses')}</Link>
        <LanguageSwitcher className="!bg-white/10 !border-white/20 !text-white" />
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm text-slate-300">{user?.fullName || user?.username}</p>
          <button onClick={() => { logout(); navigate('/admin/login') }} className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" aria-label={t('Logout')} title={t('Logout')}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden w-60 shrink-0 bg-slate-900 lg:block"><div className="sticky top-0 h-screen">{sidebar}</div></aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 start-0 w-64 bg-slate-900">{sidebar}</aside>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(!open)} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100" aria-label={t('Menu')}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <p className="text-sm font-semibold text-slate-900">{t('Rawaj Business Platform')}</p>
          <span className="w-8" />
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
